import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiAlertCircle, FiX } from "react-icons/fi";
import { AlertMsg } from "../../../shared/components/alert-msg";
import { LoadingItem } from "../../../shared/components/loading";
import { appConstants } from "../../../redux/action-constants/inapp.constants";
import {
  CreateStoreGroupPayload,
  StoreGroup,
  StoreGroupDetail,
  StoreGroupsPageProps,
  UpdateStoreGroupPayload,
} from "../../../types/app/storeGroupsTypes";
import { MediaPageProps } from "../../../types/app/mediaTypes";
import StoreTypeFormModal from "./StoreTypeFormModal";
import StoreTypeTable from "./StoreTypeTable";
import "./StoreTypeManager.scss";

type StoreTypeManagerProps = Pick<
  StoreGroupsPageProps,
  | "getStoreGroupsAction"
  | "getStoreGroupsRequest"
  | "getStoreGroupAction"
  | "getStoreGroupRequest"
  | "createStoreGroupAction"
  | "createStoreGroupRequest"
  | "updateStoreGroupAction"
  | "updateStoreGroupRequest"
  | "deleteStoreGroupAction"
  | "deleteStoreGroupRequest"
> &
  Pick<
    MediaPageProps,
    "getImageGalleryAction" | "getImageGalleryRequest"
  >;

interface ActionProps {
  pageProps: StoreTypeManagerProps;
  payload: string | "CLEAR";
}

const PAGE_PARAM = "storeTypePage";
const PAGE_SIZE = 10;

const getStoreGroupsAction = async ({ pageProps, payload }: ActionProps) => {
  await pageProps.getStoreGroupsAction(payload);
};

const getInitialPageNumber = (search: string) => {
  const pageValue = Number(new URLSearchParams(search).get(PAGE_PARAM));

  if (!Number.isFinite(pageValue) || pageValue < 1) {
    return 1;
  }

  return Math.floor(pageValue);
};

const StoreTypeManager = ({ pageProps }: { pageProps: StoreTypeManagerProps }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState<number>(
    getInitialPageNumber(location.search)
  );
  const [activeModal, setActiveModal] = useState<"create" | "edit" | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<StoreGroupDetail | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<StoreGroup | null>(null);
  const [deleteConfirmationValue, setDeleteConfirmationValue] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    getStoreGroupsAction({
      pageProps,
      payload: "CLEAR",
    });
    pageProps.createStoreGroupAction("CLEAR");
    pageProps.updateStoreGroupAction("CLEAR");
    pageProps.getStoreGroupAction("CLEAR");
    pageProps.deleteStoreGroupAction("CLEAR", "");
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set(PAGE_PARAM, pageNumber.toString());
    navigate(`?${params.toString()}`, { replace: true });
  }, [pageNumber, location.search, navigate]);

  useEffect(() => {
    getStoreGroupsAction({
      pageProps,
      payload: `Page=${pageNumber}&Size=${PAGE_SIZE}&Name=`,
    });
  }, [pageNumber]);

  useEffect(() => {
    if (
      activeModal === "create" &&
      pageProps?.createStoreGroupRequest?.request_status ===
        appConstants.CREATE_STORE_GROUP_SUCCESS
    ) {
      setSuccessMessage("Store type created successfully.");
      setActiveModal(null);
      getStoreGroupsAction({
        pageProps,
        payload: `Page=${pageNumber}&Size=${PAGE_SIZE}&Name=`,
      });
      pageProps.createStoreGroupAction("CLEAR");
    }
  }, [pageProps?.createStoreGroupRequest?.request_status, activeModal, pageNumber]);

  useEffect(() => {
    if (
      activeModal === "edit" &&
      pageProps?.updateStoreGroupRequest?.request_status ===
        appConstants.UPDATE_STORE_GROUP_SUCCESS
    ) {
      setSuccessMessage("Store type updated successfully.");
      setActiveModal(null);
      setSelectedGroup(null);
      getStoreGroupsAction({
        pageProps,
        payload: `Page=${pageNumber}&Size=${PAGE_SIZE}&Name=`,
      });
      pageProps.updateStoreGroupAction("CLEAR");
    }
  }, [pageProps?.updateStoreGroupRequest?.request_status, activeModal, pageNumber]);

  useEffect(() => {
    if (
      groupToDelete &&
      pageProps?.deleteStoreGroupRequest?.request_status ===
        appConstants.DELETE_STORE_GROUP_SUCCESS
    ) {
      setSuccessMessage(
        `${groupToDelete.name || "Store type"} deleted successfully.`
      );
      setGroupToDelete(null);
      setDeleteConfirmationValue("");
      getStoreGroupsAction({
        pageProps,
        payload: `Page=${pageNumber}&Size=${PAGE_SIZE}&Name=`,
      });
      pageProps.deleteStoreGroupAction("CLEAR", "");
    }
  }, [pageProps?.deleteStoreGroupRequest?.request_status, groupToDelete, pageNumber]);

  useEffect(() => {
    if (
      activeModal === "edit" &&
      pageProps?.getStoreGroupRequest?.request_status ===
        appConstants.GET_STORE_GROUP_SUCCESS &&
      pageProps?.getStoreGroupRequest?.request_data?.response
    ) {
      setSelectedGroup((previous) => ({
        ...(previous || {}),
        ...pageProps.getStoreGroupRequest.request_data.response,
      }));
    }
  }, [pageProps?.getStoreGroupRequest?.request_status, activeModal]);

  const response = pageProps?.getStoreGroupsRequest?.request_data?.response;
  const requestStatus = pageProps?.getStoreGroupsRequest?.request_status;
  const isLoading = pageProps?.getStoreGroupsRequest?.is_request_processing;
  const hasResults = !!response?.results?.length;

  const handlePageClick = (event: { selected: number }) => {
    setPageNumber(event.selected + 1);
  };

  const openCreateModal = () => {
    setSuccessMessage("");
    pageProps.createStoreGroupAction("CLEAR");
    setActiveModal("create");
  };

  const openEditModal = (group: StoreGroup) => {
    setSuccessMessage("");
    pageProps.updateStoreGroupAction("CLEAR");
    pageProps.getStoreGroupAction("CLEAR");
    setSelectedGroup(group as StoreGroupDetail);
    setActiveModal("edit");
    pageProps.getStoreGroupAction("", String(group.id));
  };

  const openDeleteModal = (group: StoreGroup) => {
    setSuccessMessage("");
    pageProps.deleteStoreGroupAction("CLEAR", "");
    setDeleteConfirmationValue("");
    setGroupToDelete(group);
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedGroup(null);
    pageProps.createStoreGroupAction("CLEAR");
    pageProps.updateStoreGroupAction("CLEAR");
    pageProps.getStoreGroupAction("CLEAR");
  };

  const closeDeleteModal = () => {
    if (pageProps?.deleteStoreGroupRequest?.is_request_processing) {
      return;
    }

    setGroupToDelete(null);
    setDeleteConfirmationValue("");
    pageProps.deleteStoreGroupAction("CLEAR", "");
  };

  const handleDeleteStoreType = () => {
    if (!groupToDelete?.id) {
      return;
    }

    pageProps.deleteStoreGroupAction("DELETE", String(groupToDelete.id));
  };

  const mediaPageProps = useMemo(
    () => ({
      getImageGalleryAction: pageProps.getImageGalleryAction,
      getImageGalleryRequest: pageProps.getImageGalleryRequest,
    }),
    [pageProps]
  );
  const deleteConfirmationLabel = (groupToDelete?.name || "").toUpperCase();
  const isDeleteConfirmationMatched =
    deleteConfirmationValue.trim() === deleteConfirmationLabel;

  return (
    <div className="store-type-manager">
      <div className="store-type-header">
        <div className="store-type-header-copy">
          <h2>Store type</h2>
          <p>Browse the store groups currently available in the product API.</p>
        </div>
        <div className="store-type-header-actions">
          <div className="store-type-total">
            <span>Total store types</span>
            <strong>{response?.totalNumberOfRecords ?? 0}</strong>
          </div>
          <button type="button" className="store-type-add-btn" onClick={openCreateModal}>
            Add store type
          </button>
        </div>
      </div>

      {successMessage && <AlertMsg type="success" message={successMessage} />}

      {requestStatus === appConstants.GET_STORE_GROUPS_FAILURE && (
        <AlertMsg
          type="error"
          message={pageProps?.getStoreGroupsRequest?.request_data?.error}
        />
      )}

      {!activeModal &&
        pageProps?.createStoreGroupRequest?.request_status ===
          appConstants.CREATE_STORE_GROUP_FAILURE && (
          <AlertMsg
            type="error"
            message={pageProps?.createStoreGroupRequest?.request_data?.error}
          />
        )}

      {!activeModal &&
        pageProps?.updateStoreGroupRequest?.request_status ===
          appConstants.UPDATE_STORE_GROUP_FAILURE && (
          <AlertMsg
            type="error"
            message={pageProps?.updateStoreGroupRequest?.request_data?.error}
          />
        )}

      {!groupToDelete &&
        pageProps?.deleteStoreGroupRequest?.request_status ===
          appConstants.DELETE_STORE_GROUP_FAILURE && (
          <AlertMsg
            type="error"
            message={pageProps?.deleteStoreGroupRequest?.request_data?.error}
          />
        )}

      {isLoading && <LoadingItem />}

      {!isLoading &&
        requestStatus === appConstants.GET_STORE_GROUPS_SUCCESS &&
        hasResults &&
        response && (
          <StoreTypeTable
            response={response}
            pageNumber={pageNumber}
            onPageChange={handlePageClick}
            onEdit={openEditModal}
            onDelete={openDeleteModal}
            deletingGroupId={
              pageProps?.deleteStoreGroupRequest?.is_request_processing
                ? groupToDelete?.id ?? null
                : null
            }
          />
        )}

      {!isLoading &&
        requestStatus === appConstants.GET_STORE_GROUPS_SUCCESS &&
        !hasResults && (
          <div className="store-type-empty-state">
            <p>No store types found.</p>
          </div>
        )}

      <StoreTypeFormModal
        show={activeModal === "create"}
        mode="create"
        onClose={closeModal}
        onCreate={(payload: CreateStoreGroupPayload) =>
          pageProps.createStoreGroupAction(payload)
        }
        onUpdate={(payload: UpdateStoreGroupPayload) =>
          pageProps.updateStoreGroupAction(payload)
        }
        mutationRequest={pageProps.createStoreGroupRequest}
        mediaPageProps={mediaPageProps}
      />

      <StoreTypeFormModal
        show={activeModal === "edit"}
        mode="edit"
        onClose={closeModal}
        initialGroup={selectedGroup}
        onCreate={(payload: CreateStoreGroupPayload) =>
          pageProps.createStoreGroupAction(payload)
        }
        onUpdate={(payload: UpdateStoreGroupPayload) =>
          pageProps.updateStoreGroupAction(payload)
        }
        mutationRequest={pageProps.updateStoreGroupRequest}
        mediaPageProps={mediaPageProps}
      />

      {groupToDelete && (
        <div className="store-type-form-overlay">
          <div className="store-type-delete-modal">
            <div className="store-type-delete-hero">
              <div className="store-type-delete-badge">DEL</div>
            </div>

            <div className="store-type-delete-header">
              <h3>Delete store type</h3>
              <button
                type="button"
                onClick={closeDeleteModal}
                className="close-button"
                disabled={pageProps?.deleteStoreGroupRequest?.is_request_processing}
                aria-label="Close delete confirmation"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="store-type-delete-body">
              {pageProps?.deleteStoreGroupRequest?.request_status ===
                appConstants.DELETE_STORE_GROUP_FAILURE && (
                <AlertMsg
                  type="error"
                  message={pageProps?.deleteStoreGroupRequest?.request_data?.error}
                />
              )}

              <p className="store-type-delete-subtext">
                This action is permanent and cannot be undone.
                <br />
                All stores under this type will be unassigned.
              </p>

              <div className="store-type-delete-impact">
                <h4>What will happen?</h4>
                <div className="store-type-delete-impact-list">
                  <div className="store-type-delete-impact-item">
                    <span className="impact-icon">
                      <FiAlertCircle size={14} />
                    </span>
                    <p>
                      The "{groupToDelete.name || "Store type"}" store type will be
                      permanently deleted.
                    </p>
                  </div>
                  <div className="store-type-delete-impact-item">
                    <span className="impact-icon">
                      <FiAlertCircle size={14} />
                    </span>
                    <p>
                      Stores using this type will lose it and show as
                      "Categorized".
                    </p>
                  </div>
                  <div className="store-type-delete-impact-item">
                    <span className="impact-icon">
                      <FiAlertCircle size={14} />
                    </span>
                    <p>Filters or reports referencing this type will be affected.</p>
                  </div>
                </div>
              </div>

              <div className="store-type-delete-confirmation">
                <label htmlFor="delete-store-type-confirmation">
                  Type "{deleteConfirmationLabel}" to confirm deletion
                </label>
                <input
                  id="delete-store-type-confirmation"
                  type="text"
                  value={deleteConfirmationValue}
                  onChange={(event) => setDeleteConfirmationValue(event.target.value)}
                  placeholder={`Type "${deleteConfirmationLabel}" here...`}
                  disabled={pageProps?.deleteStoreGroupRequest?.is_request_processing}
                />
              </div>

              <div className="store-type-delete-actions">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={closeDeleteModal}
                  disabled={pageProps?.deleteStoreGroupRequest?.is_request_processing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="danger-btn"
                  onClick={handleDeleteStoreType}
                  disabled={
                    pageProps?.deleteStoreGroupRequest?.is_request_processing ||
                    !isDeleteConfirmationMatched
                  }
                >
                  {pageProps?.deleteStoreGroupRequest?.is_request_processing
                    ? "Deleting store type..."
                    : "Delete store type"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreTypeManager;
